import React, { useContext, useEffect, useState } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { podFulFillmentUtils } from '@thd-olt-component-react/fulfillment';
import { KeyProductFeatures } from '@thd-olt-component-react/key-product-features';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';

import { ProductPodContext } from '../../ProductPodContext';
import { ProductATC } from '../product-atc/ProductATC';
import { ProductBadge } from '../product-badge/ProductBadge';
import { ProductCompare } from '../product-compare/ProductCompare';
import { ProductCustomOptions } from '../product-custom-options/ProductCustomOptions';
import { ProductFavorites } from '../product-favorites/ProductFavorites';
import { ProductHeader } from '../product-header/ProductHeader';
import { ProductImage } from '../product-image/ProductImage';
import { ProductModel } from '../product-model/ProductModel';
import { ProductPrice } from '../product-price/ProductPrice';
import { ProductRating } from '../product-rating/ProductRating';
import { ProductServiceAddOns } from '../product-service-add-ons/ProductServiceAddOns';
import { ProductRemove } from '../product-remove/ProductRemove';
import { ProductSample } from '../product-sample/ProductSample';
import { ProductSeeSimilarItems } from '../product-see-similar-items/ProductSeeSimilarItems';
import { ProductShipping } from '../product-shipping/ProductShipping';
import { ProductSponsored } from '../product-sponsored/ProductSponsored';
import { ProductQuantity } from '../product-quantity/ProductQuantity';
import { shouldShowQuantityBox } from '../../utils';
import { ProductStore } from '../product-store/ProductStore';
import './ProductDefault.style.scss';

export const ProductDefault = () => {
  const {
    buyMoreSaveMore,
    canonicalUrl,
    channelFromTest,
    comparable,
    hideATC,
    hideLimitPerOrder,
    hidePriceBadge,
    hideSavingsText,
    hideShippingInfo,
    hideShopThisCollection,
    hover,
    isBOLDCategory,
    isHovered,
    itemId,
    mobilePodType,
    onChangeSuperSku,
    onFavoriteChange,
    onHoverSuperSku,
    position,
    previousItemId,
    product,
    removable,
    showKPF,
    showSample,
    showModelNumber,
    showQuantityBox,
    showShippingInfoInHover,
    showStoreInfo,
    storeId,
    swatchCount,
    showSimilarItemButton
  } = useContext(ProductPodContext);

  const { badges, info, identifiers } = product || {};
  const { productType } = identifiers || {};
  const superSkuItemId = isHovered ? previousItemId : itemId;
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile' || channelFromTest === 'mobile';
  const mobileDoubleColumn = mobilePodType === 'doubleColumn' && channel === 'mobile';
  const brandStyle = hover ? 'below' : 'inline';

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const showShipping = !hideShippingInfo && !showShippingInfoInHover && !info?.isBrioSku;
  const showHoverBehavior = mounted
    && hover
    && !isMobile;
  const showBoldCategoryStore = mounted
    && isBOLDCategory
    && !podFulFillmentUtils.isDiscontinued(product)
    && !isMobile;
  const showStore = showStoreInfo && !info?.isBrioSku;
  const showSimilarLink = !showSimilarItemButton && mobileDoubleColumn;
  const showFulfillmentSection = showShipping || showStore;

  return (
    <>
      <div className="product-pod__badge--wrapper" data-testid="product-default-badge">
        <ProductBadge badges={badges} />
      </div>
      <ProductFavorites
        className="product-pod__inline__favorite"
        onFavoriteChange={onFavoriteChange}
        position="topRight"
        showCircle
      />
      <ProductImage buyMoreSaveMore={buyMoreSaveMore} />
      <ProductCustomOptions />
      <div className="product-pod--padding">
        {mounted && (
          <SuperSkuMiniSwatches
            itemId={superSkuItemId}
            canonicalUrl={canonicalUrl}
            storeId={storeId}
            onChange={onChangeSuperSku}
            onHover={onHoverSuperSku}
            numberOfMiniSwatches={swatchCount}
          />
        )}
        {showSample && <ProductSample />}
        <ProductSponsored position={position} />

        <ProductHeader
          brand={brandStyle}
          disableShopThisCollection={hideShopThisCollection}
        />

        <div className="ratings-and-model-number-container">
          <ProductRating />
          {showModelNumber && <ProductModel />}
        </div>
        <ProductServiceAddOns />
        <ProductPrice
          className="product-pod__pricing"
          hideBadge={hidePriceBadge}
          hideLimitPerOrder={hideLimitPerOrder}
          hideSavingsText={hideSavingsText}
          large={false}
        />

        {showKPF && (
          <KeyProductFeatures
            hideImage
            storeId={storeId}
            itemId={itemId}
            maxfeatures={5}
            simple
            oneColumnGrid
          />
        )}

        {showShipping && hover && <ProductShipping className="product-pod__fulfillment--shipping" />}

        {mounted && showSimilarLink && <ProductSeeSimilarItems isMobileDoubleColumn />}

        {showBoldCategoryStore && <ProductStore checkGeneric />}

        {showHoverBehavior && (
          <div className="product-pod__toggle">
            <div className="product-pod__toggle__wrapper">
              {!podFulFillmentUtils.isDiscontinued(product) ? (
                <>
                  {showShippingInfoInHover && <ProductShipping className="product-pod__fulfillment--shipping" />}
                  {!isBOLDCategory && <ProductStore checkGeneric />}
                  {mounted && <ProductSeeSimilarItems />}
                  <ProductATC className="product-pod__add-to-cart" outline checkGeneric />
                </>
              ) : (<ProductSeeSimilarItems />)}
            </div>
          </div>
        )}

        {mounted && mobileDoubleColumn && (
          <>
            {showSimilarItemButton && <ProductSeeSimilarItems isMobileDoubleColumn />}
            <ProductATC
              className="product-pod__add-to-cart--mobile"
              outline
              checkGeneric
            />

            {comparable && <ProductCompare />}
            {removable && <ProductRemove />}
          </>
        )}

        {mounted && !mobileDoubleColumn && !hover && (
          <>
            {showFulfillmentSection && (
              <div className="product-pod__fulfillment--wrapper">
                {showShipping && <ProductShipping className="product-pod__fulfillment--shipping" />}
                {showStore && <ProductStore checkGeneric />}
              </div>
            )}
            {!hideATC && (
              <div className="product-pod__add-to-cart-wrapper">
                {<ProductSeeSimilarItems />}
                {showQuantityBox && shouldShowQuantityBox(product) ? (
                  <div className="product-pod__add-to-cart-quantity__wrapper">
                    <ProductQuantity textOnly wideView showQtyText smallText shortHeight noRightMargin />
                    <div className="product-pod__add-to-cart-quantity__add-to-cart-wrapper">
                      <ProductATC className="product-pod__add-to-cart" outline checkGeneric />
                    </div>
                  </div>
                ) : (
                  <ProductATC className="product-pod__add-to-cart" outline checkGeneric />
                )}
              </div>
            )}
            {comparable && <ProductCompare />}
            {removable && <ProductRemove />}
          </>
        )}
      </div>
    </>
  );
};

ProductDefault.displayName = 'ProductPodDefault';
